export type AssistantMessageLlmUsage = {
  resultCacheHit: boolean;
  inputTokens: number;
  outputTokens: number;
  /** Provider prompt-cache read tokens (Anthropic/OpenAI cache hit). */
  cachedInputTokens: number;
};

export type AssistantMessageLlmUsageInput = {
  inputTokens?: number;
  outputTokens?: number;
  cachedInputTokens?: number;
};

export function buildAssistantMessageLlmUsage(
  usage: AssistantMessageLlmUsageInput,
  resultCacheHit: boolean
): AssistantMessageLlmUsage {
  return {
    resultCacheHit,
    inputTokens: usage.inputTokens ?? 0,
    outputTokens: usage.outputTokens ?? 0,
    cachedInputTokens: usage.cachedInputTokens ?? 0,
  };
}

export function parseAssistantMessageLlmUsage(
  raw: unknown
): AssistantMessageLlmUsage | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const hasResultCacheHit = typeof record['resultCacheHit'] === 'boolean';
  const hasInputTokens = typeof record['inputTokens'] === 'number';
  const hasOutputTokens = typeof record['outputTokens'] === 'number';
  const hasCachedInputTokens = typeof record['cachedInputTokens'] === 'number';

  if (
    !hasResultCacheHit &&
    !hasInputTokens &&
    !hasOutputTokens &&
    !hasCachedInputTokens
  ) {
    return null;
  }

  return {
    resultCacheHit: record['resultCacheHit'] === true,
    inputTokens: hasInputTokens ? (record['inputTokens'] as number) : 0,
    outputTokens: hasOutputTokens ? (record['outputTokens'] as number) : 0,
    cachedInputTokens: hasCachedInputTokens
      ? (record['cachedInputTokens'] as number)
      : 0,
  };
}

export function buildLlmUsageByMessageId(
  messages: ReadonlyArray<{
    id?: string;
    getLlmUsage(): AssistantMessageLlmUsage | null;
  }>
): Record<string, AssistantMessageLlmUsage> {
  const usageByMessageId: Record<string, AssistantMessageLlmUsage> = {};

  for (const message of messages) {
    const usage = message.getLlmUsage();
    if (usage === null) {
      continue;
    }
    const messageId = message.id;
    if (messageId === undefined) {
      continue;
    }
    usageByMessageId[messageId] = usage;
  }

  return usageByMessageId;
}
